import React, { useEffect, useRef, useState } from "react";
import styles from "./AvatarWidget.module.scss";
import { t } from "../i18n";
import { useGuidedTour } from "../GuidedTourProvider";

/**
 * Draggable tour widget. Bottom-center by default; drag the widget header
 * to reposition. Snaps back inside the viewport on release.
 */
const WIDGET_W = 320;
const WIDGET_H = 156;
const MARGIN = 12;

const AvatarWidget = ({ body, hint, stepIndex, totalSteps, onPrev, onNext, onClose, canGoPrev, canGoNext }) => {
  const { muted, toggleMuted } = useGuidedTour();
  const [pos, setPos] = useState(() => ({
    x: Math.max(MARGIN, (window.innerWidth - WIDGET_W) / 2),
    y: window.innerHeight - WIDGET_H - 96,
  }));
  const dragState = useRef({ dragging: false, offsetX: 0, offsetY: 0 });

  const handleMouseDown = (e) => {
    dragState.current.dragging = true;
    dragState.current.offsetX = e.clientX - pos.x;
    dragState.current.offsetY = e.clientY - pos.y;
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const handleMove = (e) => {
      if (!dragState.current.dragging) return;
      setPos({
        x: e.clientX - dragState.current.offsetX,
        y: e.clientY - dragState.current.offsetY,
      });
    };
    const handleUp = () => {
      if (!dragState.current.dragging) return;
      dragState.current.dragging = false;
      document.body.style.userSelect = "";
      // Snap into viewport
      setPos((p) => ({
        x: Math.min(Math.max(MARGIN, p.x), window.innerWidth - WIDGET_W - MARGIN),
        y: Math.min(Math.max(60, p.y), window.innerHeight - WIDGET_H - MARGIN),
      }));
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  const progressPct = totalSteps > 0 ? ((stepIndex + 1) / totalSteps) * 100 : 0;
  // Desktop is LTR by default in this codebase; icons don't need flipping.

  return (
    <>
      <div
        className={styles.widget}
        style={{ transform: `translate(${pos.x}px, ${pos.y}px)`, width: WIDGET_W }}
      >
        <button className={styles.close} onClick={onClose} aria-label="Close tour">×</button>

        <div className={styles.dragHandle} onMouseDown={handleMouseDown} title="Drag" />

        <div className={styles.memoji}>
          <span role="img" aria-label="Guide">👩🏻‍💻</span>
        </div>

        <p className={styles.body}>{body}</p>

        <div className={styles.progress}>
          <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
        </div>

        <div className={styles.controls}>
          <button
            className={styles.ctrlBtn}
            onClick={canGoPrev ? onPrev : undefined}
            disabled={!canGoPrev}
            aria-label={t("tours.player.previous")}
          >
            ‹
          </button>
          <button
            className={styles.ctrlBtn}
            onClick={canGoNext ? onNext : undefined}
            disabled={!canGoNext}
            aria-label={t("tours.player.next")}
          >
            ›
          </button>
          <div className={styles.spacer} />
          <button
            className={styles.muteBtn}
            onClick={toggleMuted}
            aria-label={muted ? "Unmute voiceover" : "Mute voiceover"}
            title={muted ? "Unmute" : "Mute"}
          >
            {muted ? "🔇" : "🔊"}
          </button>
        </div>
      </div>

      {hint ? (
        <div
          className={styles.hintPill}
          style={{ transform: `translate(${pos.x}px, ${pos.y + WIDGET_H + 8}px)` }}
        >
          <span className={styles.hintDot} />
          <span>{hint}</span>
        </div>
      ) : null}
    </>
  );
};

export default AvatarWidget;
